import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import moment from 'moment';
import DateHeader from './DateHeader';
import DateTable from './DateTable';
import Navigator from './Navigator';
import styles from './index.module.scss';
import { DateCeilType, getTodayTime, getCurMonthAry } from './util';
import getCurDateHook from './getCurDateHook';

export interface CalendarRefProps {
  setDate(date: number): void;
  setSelectDate(date: number): void;
}

type IProps = {
  onSelectDate?: (date: number) => void;
  noticeList: number[];
  taskList: number[];
  onSwitch?: (startTime: number, endTime: number) => void;
}

const Calendar = forwardRef<CalendarRefProps, IProps>(({ onSelectDate, noticeList, taskList, onSwitch }, ref) => {
  const [date, setDate] = useState(getTodayTime());
  const [selectDate, setSelectDate] = useState(getTodayTime());
  const today = getCurDateHook(getTodayTime());

  const _getCurMonthAry = useCallback(
    () => getCurMonthAry(date),
    [date]
  );

  const ceilAry = _getCurMonthAry();

  useEffect(() => {
    onSwitch && onSwitch(ceilAry[0].date, ceilAry[ceilAry.length - 1].date);
  }, []);

  useImperativeHandle(ref, () => ({
    setDate: (date: number) => setDate(date),
    setSelectDate: (date: number) => setSelectDate(date)
  }));

  const _prev = () => {
    const startOfPrevMonth = moment(date).subtract(1, 'M').startOf('month').valueOf();
    const ceilAry = getCurMonthAry(startOfPrevMonth);
    setDate(startOfPrevMonth);
    onSwitch && onSwitch(ceilAry[0].startTime, ceilAry[ceilAry.length - 1].endTime);
  };

  const _today = () => {
    const today = getTodayTime();
    const ceilAry = getCurMonthAry(today);
    setDate(today);
    setSelectDate(today);
    onSelectDate && onSelectDate(today);
    onSwitch && onSwitch(ceilAry[0].startTime, ceilAry[ceilAry.length - 1].endTime);
  };

  const _next = () => {
    const startOfNextMonth = moment(date).add(1, 'M').startOf('month').valueOf();
    const ceilAry = getCurMonthAry(startOfNextMonth);
    setDate(startOfNextMonth);
    onSwitch && onSwitch(ceilAry[0].startTime, ceilAry[ceilAry.length - 1].endTime);
  };

  const _onSelectDate = (data: DateCeilType | undefined) => {
    if (data) {
      const curM = moment(date).month();

      if (
        data.month === 0 &&
          curM === 11
      ) {
        _next();
      } else if (
        data.month === 11 &&
        curM === 0
      ) {
        _prev();
      } else if (data.month > curM) {
        _next();
      } else if (data.month < curM) {
        _prev();
      }

      setSelectDate(data.date);
      onSelectDate && onSelectDate(data.date);
    }
  };

  return (
    <div className={styles.calendar}>
      <Navigator
        dateFormat={moment(date).format('YYYY年MM月')}
        prev={_prev}
        today={_today}
        next={_next}
      />
      <DateHeader />
      <DateTable
        today={today}
        noticeList={noticeList}
        taskList={taskList}
        ceilAry={_getCurMonthAry()}
        selectedDate={selectDate}
        onSelectDate={_onSelectDate}
      />
    </div>
  );
});

Calendar.displayName = 'Calendar';
export default Calendar;
